import React, { FC, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Switch,
  Checkbox,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import LaptopIcon from '@mui/icons-material/Laptop';
import TimerIcon from '@mui/icons-material/Timer';
import ShieldIcon from '@mui/icons-material/Shield';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import { BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
// Map identity — the research-consent split is per-map (Merck/BCAN on bladder cancer, Research
// Connect Direct everywhere else), same rule as OnboardingConsent.
import { baseName } from '../..';
import { useAiSummaryEnabled, setAiSummaryEnabled } from './aiSettings';
import {
  useSetBrowserTrustMutation,
  useGetOnboardingStatusQuery,
  useDeleteHWDataMutation,
  useRevokeResearchConsentMutation,
  useRequestHealthDataMutation,
  useSetDedupMutation,
  healthWalletApi,
} from './healthWalletSlice';
import { useAppDispatch, useAuthProfile } from '../../app/hooks';
import { DataRequestSSEUpdates } from './DataRequestSSEUpdates';
import { clearSyncLog } from './healthWalletSyncService';
import type { EpicFhirOrg } from '../../types/onboarding';
import { ProviderOrgList, ProviderRefreshButton, providerRetrieveLabel, orgKey, scrollOrgIntoView } from './ProviderOrgRow';
import { MissingProviderPicker } from './MissingProviderPicker';
import { ProviderRemoveButton } from './ProviderRemoveButton';
import { LongPressGuard } from '../../app/components/LongPressGueard';

function getLastUpdatedMessage(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Your records are up to date';
  if (minutes < 60) return `Last updated ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Last updated ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `Last updated ${days} day${days === 1 ? '' : 's'} ago`;
}

interface HealthWalletSettingsProps {
  open: boolean;
  onClose: () => void;
}

export const HealthWalletSettings: FC<HealthWalletSettingsProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { authStatus, profile: loggedInUserProfile, isLoading: isLoadingAuth, isSuccess: isSuccessAuth } = useAuthProfile();
  const isAuthenticated = !isLoadingAuth && isSuccessAuth && authStatus === 'authenticated';
  const profileId: number | undefined = loggedInUserProfile?.id;

  const { data: onboardingStatus, isLoading: isOnboardingLoading } = useGetOnboardingStatusQuery();
  const [setBrowserTrust, { isLoading: isTrustLoading, isError: isTrustError, error: trustError }] = useSetBrowserTrustMutation();
  const [deleteHWData, { isLoading: isDeleting, isError: isDeleteError, error: deleteError }] = useDeleteHWDataMutation();
  const [revokeResearchConsent, { isLoading: isRevoking, isError: isRevokeError, error: revokeError }] = useRevokeResearchConsentMutation();
  const [requestHealthData] = useRequestHealthDataMutation();

  // Derived status
  const dataRequestStatus = onboardingStatus?.dataRequestStatus;
  const isDataRequestPending = dataRequestStatus === 'pending' || dataRequestStatus === 'processing';
  const isDataRequestComplete = dataRequestStatus === 'completed';
  const isDataRequestFailed = dataRequestStatus === 'failed';
  const lastCompletedAt = onboardingStatus?.lastCompletedAt ?? null;
  // Per-workflow pending flags — used so each button only shows its own spinner.
  // Both buttons stay disabled while either workflow is pending (via isDataRequestPending)
  // to prevent concurrent runs.
  const dataRequestType = onboardingStatus?.dataRequestType ?? null;
  const isIasRequestPending = isDataRequestPending && dataRequestType?.toLowerCase().includes('ias');
  const isEpicRequestPending = isDataRequestPending && dataRequestType?.toLowerCase().includes('epic');

  // SSE state
  const [sseEverShown, setSseEverShown] = useState(false);
  const [showLog, setShowLog] = useState(false);


  // Minute tick so "last updated" stays current
  const [, setMinuteTick] = useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setMinuteTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Auto-show SSE log on page load if a request is already in flight
  React.useEffect(() => {
    if (isDataRequestPending || isDataRequestFailed) setSseEverShown(true);
  }, [isDataRequestPending, isDataRequestFailed]);

  React.useEffect(() => {
    if (!open) setConfirmDelete(false);
  }, [open]);

  const trustEnabled = onboardingStatus?.browserTrusted ?? false;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const isBladderCancerMap = baseName === 'bladder-cancer';
  const [bypassEpicDataRequest, setBypassEpicDataRequest] = useState(false);
  // Advanced (dev) toggle — session-only, no storage. Threads forceReprocessCcda into the data-request
  // payload so the Epic workflow re-fetches + overwrites already-stored CCDAs instead of the write-once
  // skip (Kno2 already reprocesses every sync). See project_hw_force_refetch_reprocess_ccda.
  const [forceReprocessAll, setForceReprocessAll] = useState(false);
  // Read-time dedup dev toggle (removed for prod with the Login toggle). SET-only backend: resting state
  // is TRUE on both sides, so this initializes ON with no read; the mutation invalidates ['Patient'] so
  // records refetch in the new mode. A page reload after flipping OFF shows ON until re-flipped (or HW
  // restarts, which also resets to ON) — accepted for a dev instrument. project_hw_resource_dedup_feature.
  const [dedupOn, setDedupOn] = useState(true);
  const aiSummaryOn = useAiSummaryEnabled(); // memory-only module flag (aiSettings.ts)
  // Advanced (dev toggles) HIDDEN in PROD builds — REACT_APP_ENV (build:prod/test), same gate the nav uses.
  // Escape hatch: a 5s long-press on the settings background flips revealAdvanced (session-only, no storage/
  // backend), same LongPressGuard mechanism the navbar uses to open AuthProfileDialog. Resets on reload.
  const [revealAdvanced, setRevealAdvanced] = useState(false);
  const hideAdvanced = process.env.REACT_APP_ENV === 'prod' && !revealAdvanced;
  const [setDedup] = useSetDedupMutation();
  const handleDedupToggle = async (checked: boolean) => {
    setDedupOn(checked);
    try {
      await setDedup({ dedup: checked }).unwrap();
    } catch (e) {
      console.error('[HealthWalletSettings] dedup toggle failed:', e);
      setDedupOn(!checked); // revert on failure
    }
  };
  // Which per-org Epic button was clicked, so only THAT org's button spins while all stay disabled.
  // Stale value is harmless: the spinner is also gated on isEpicRequestPending, so it only renders
  // while a refresh is actually in flight, and it's overwritten on the next click.
  const [refreshingOrgKey, setRefreshingOrgKey] = useState<string | null>(null);

  // Row to flash after the picker adds a provider (or reports one was already there) — cleared on a
  // timer so it reads as "here it is" rather than a stuck selection.
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  React.useEffect(() => {
    if (!highlightKey) return;
    const t = setTimeout(() => setHighlightKey(null), 4000);
    return () => clearTimeout(t);
  }, [highlightKey]);


  const handleTrustChange = async (checked: boolean) => {
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        draft.browserTrusted = checked;
      })
    );
    try {
      const result = await setBrowserTrust({ trust: checked }).unwrap();
      if (result?.state) {
        dispatch(
          healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
            Object.assign(draft, result.state);
          })
        );
      }
    } catch {
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          draft.browserTrusted = !checked;
        })
      );
    }
  };

const handleRestartWorkflow = async () => {
    clearSyncLog(dispatch); // clear the old run's progress log immediately on click
    setSseEverShown(true);
    if (lastCompletedAt !== null) setShowLog(true);

    // NO click-time status patch (it kept the OLD dataRequestId → forged the SSE snapshot merge's
    // race signature → spurious refetch). The response patch below is status-ATOMIC-with-id and
    // carries the DB-computed stopwatch seed. See project_hw_seed_optimistic_interaction.
    try {
      const result = await requestHealthData(forceReprocessAll ? { forceReprocessCcda: true } : undefined).unwrap();
      if (result?.state) {
        dispatch(
          healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
            Object.assign(draft, result.state);
          })
        );
      }
    } catch (err) {
      console.error('[HealthWalletSettings] Failed to request health data:', err);
      // Surface synchronous mutation failures via the generic isDataRequestFailed alert.
      // (Background workflow failures already flow through SSE → dataRequestStatus='failed'.)
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          draft.dataRequestStatus = 'failed';
          draft.error = (err as any)?.data?.error || (err as any)?.message || 'Failed to retrieve health records.';
        })
      );
    }
  };

  // A provider the PATIENT named via the "missing provider?" picker. Append the row the SERVER
  // returned (already in epic_fhir_orgs shape) rather than assembling one here — a locally-built copy
  // is how a parallel shape drifts from the server's definition. It arrives needs_auth, so the row's
  // normal Connect affordance takes over from here.
  const handleProviderAdded = (org: EpicFhirOrg) => {
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        if (!draft) return;
        if (!draft.epicFhirOrgs) draft.epicFhirOrgs = [];
        if (!draft.epicFhirOrgs.some((o) => o.requestId === org.requestId && o.index === org.index)) {
          draft.epicFhirOrgs.push(org);
        }
      })
    );
    // Show them the row. Unlike onboarding, nothing here changes screens, so without this the only
    // feedback is a notice above a list the new row may have landed below.
    const key = orgKey(org);
    setHighlightKey(key);
    scrollOrgIntoView(key);
  };

  // Already on the list — nothing was written, so point at the row they already have.
  const handleProviderAlreadyPresent = (orgIndex: number) => {
    const existing = onboardingStatus?.epicFhirOrgs?.find((o) => o.index === orgIndex);
    if (!existing) return;
    const key = orgKey(existing);
    setHighlightKey(key);
    scrollOrgIntoView(key);
  };

  const handleEpicWorkflow = async (org?: EpicFhirOrg) => {
    if (!loggedInUserProfile?.id) {
      console.error('Profile ID not available');
      return;
    }
    // Track which org is refreshing so only its button spins (others stay disabled, not spinning).
    setRefreshingOrgKey(org ? `${org.requestId}:${org.index}` : null);
    try {
      // Reveal the SSE log. The shared workflow-progress connection is always-open now,
      // so there's no per-workflow remount/reconnect to force.
      clearSyncLog(dispatch); // clear the old run's progress log immediately on click
      setSseEverShown(true);
      if (lastCompletedAt !== null) setShowLog(true);

      // NO click-time status patch. Patching 'pending' here with the OLD dataRequestId forged the
      // SSE snapshot merge's reload-race signature (snapshot terminal + cache non-terminal, same id)
      // → a spurious status refetch at click time → the re-verify screen flashed right before the
      // Epic redirect. ALL cache patches now come from the RESPONSE, status ATOMIC with the run's
      // REAL id: authenticated → {pending, new run id}; needs_auth → {auth_pending, intent row id}.
      // See project_hw_seed_optimistic_interaction. (Button feedback meanwhile = refreshingOrgKey.)

      // Token-first: try refreshing the stored Epic token and running the workflow WITHOUT a
      // re-login — for BOTH the generic refresh and the per-org Refresh button. The "Login to
      // MyChart" toggle sends forceReauth instead: HW skips the token attempt, pre-creates the
      // auth_pending INTENT row, and answers needs_auth + its id immediately (so even a forced
      // fresh login gets the run's id before the redirect).
      // Pass the clicked org so the token-first refresh is ORG-SCOPED: HW resolves
      // orgs[orgIndex] → hcid → that org's verification (else it'd refresh the latest-active org —
      // the multi-org bug where OptumCare's button ran Hackensack). Omitted for the generic button
      // (no org) → HW keeps latest-active. Same {iasRequestId, orgIndex} contract as the redirect below.
      // NOTE (multi-org limitation, TODO): the cached-token path is only org-scoped via this
      // resolution; see prior note history in git if revisiting.
      const result = await requestHealthData({
        type: 'epic',
        ...(org?.requestId ? { iasRequestId: org.requestId, orgIndex: org.index } : {}),
        ...(bypassEpicDataRequest ? { forceReauth: true } : {}),
        ...(forceReprocessAll ? { forceReprocessCcda: true } : {}),
      }).unwrap();
      if (result?.state) {
        dispatch(
          healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
            Object.assign(draft, result.state);
          })
        );
      }

      if ( result?.state?.epicAuthStatus !== 'needs_auth') {
        // Token was valid or refreshed; workflow started in the background.
        // Button stays disabled via isDataRequestPending until the SSE 'workflow-completed'
        // event flips dataRequestStatus to 'completed' in the cache.
        return;
      }

      // needs_auth: the state assign above already applied the truthful pre-redirect patch
      // ({dataRequestStatus:'auth_pending', dataRequestId:<intent row>}). Capture the intent id to
      // thread into the form-POST → OAuth state → exchange-code promotes THAT row (no new create).
      const intentRequestId = result?.state?.dataRequestId ?? null;

      // Refresh failed (or bypass is on) — fall through to the OAuth redirect (hidden form POST, same pattern as CLEAR).
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/api/healthwallet/onboarding/verify';
      form.style.display = 'none';

      // Pass id (profileId) to match CLEAR pattern
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.value = loggedInUserProfile.id;
      form.appendChild(idInput);

      // Pass type=epic to backend to route to Epic instead of CLEAR
      const typeInput = document.createElement('input');
      typeInput.type = 'hidden';
      typeInput.name = 'type';
      typeInput.value = 'epic';
      form.appendChild(typeInput);

      // Per-org connect: pass the source request_id + the org's index within that request's
      // patientDiscovery.orgs[]. The backend resolves orgs[index] → its FHIR REST serviceAddress
      // server-side to target this specific Epic org. Both omitted for the generic refresh button.
      // See project_kno2_related_transactions_fhir.
      if (org) {
        const reqIdInput = document.createElement('input');
        reqIdInput.type = 'hidden';
        reqIdInput.name = 'iasRequestId';
        reqIdInput.value = String(org.requestId ?? '');
        form.appendChild(reqIdInput);

        const orgIndexInput = document.createElement('input');
        orgIndexInput.type = 'hidden';
        orgIndexInput.name = 'orgIndex';
        orgIndexInput.value = String(org.index);
        form.appendChild(orgIndexInput);
      }

      // The pre-created auth_pending intent row's id — rides the OAuth state so exchange-code runs
      // the workflow on THAT row (one id per run, end to end). See project_hw_seed_optimistic_interaction.
      if (intentRequestId) {
        const intentInput = document.createElement('input');
        intentInput.type = 'hidden';
        intentInput.name = 'intentRequestId';
        intentInput.value = String(intentRequestId);
        form.appendChild(intentInput);
      }

      document.body.appendChild(form);
      // EPIC-REDIRECT-DEBUG: this page becomes the Referer the map server reads to
      // compose the Epic redirect_uri (callback). Log it so we can trace the full
      // chain: this page → Referer → map frontendUrl → admin resolved redirect_uri → Epic.
      // (Remove once the Epic redirect_uri registration is confirmed working.)
      console.log('[EpicRedirectDebug][frontend] initiating Epic OAuth form POST', {
        pageHref: window.location.href,
        origin: window.location.origin,
        pathname: window.location.pathname,
        formAction: form.action,
      });
      form.submit();  // Backend will redirect to Epic MyChart
      document.body.removeChild(form);
    } catch (err) {
      console.error('Epic workflow error:', err);
      // Surface synchronous mutation failures via the generic isDataRequestFailed alert.
      // (Background workflow failures already flow through SSE → dataRequestStatus='failed'.)
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          draft.dataRequestStatus = 'failed';
          draft.error = (err as any)?.data?.error || (err as any)?.message || 'Failed to refresh Epic health records.';
        })
      );
    }
  };

  // ── Research-consent withdrawal ────────────────────────────────────────────────────────────────
  // Which optional research consent this map offers — the same split as OnboardingConsent: the
  // Merck/BCAN study is NMIBC-only, Research Connect Direct is every other map.
  const researchConsent = isBladderCancerMap
    ? { key: 'merck_study', label: 'Share my records, with the direct identifiers removed, with the Merck / BCAN bladder cancer (NMIBC) research study', doc: 'merck-study-consent.html' }
    : { key: 'research_connect_direct', label: 'Make my records, with the direct identifiers removed, available to qualified researchers through Research Connect Direct', doc: 'research-connect-direct-consent.html' };
  // Absent key = not shared (the revoke deletes the key rather than storing false — see
  // revokePatientResearchConsent). So `granted` is the single source of truth for the box.
  const researchConsentGranted = (onboardingStatus?.consents as any)?.[researchConsent.key]?.granted === true;

  const handleRevokeResearchConsent = async () => {
    if (!confirmRevoke) {
      setConfirmRevoke(true);
      return;
    }
    try {
      const res = await revokeResearchConsent({ consentKey: researchConsent.key }).unwrap();
      // Patch the cache from the server's authoritative state. Required because the key was DELETED
      // server-side and mergeOnboardingState merges consents by spread — a refetch alone could race
      // the write, and the stale `granted: true` would otherwise linger for the session.
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          if (res?.state?.consents) draft.consents = res.state.consents;
        })
      );
      setConfirmRevoke(false);
    } catch {
      // error state surfaces via isRevokeError/revokeError below
    }
  };

  const handleDeleteClick = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    if (profileId == null) return;
    try {
      await deleteHWData({ id: profileId }).unwrap();
      dispatch(
        healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
          draft.isOnboarded = false;
          draft.dataRequestStatus = 'data_deleted';
          draft.lastCompletedAt = null;
        })
      );
      setConfirmDelete(false);
      onClose();
    } catch {
      // error state surfaces via isDeleteError/deleteError below
    }
  };

  // Drawer width: 400 was tight once the provider list moved in here — long org names, the address line
  // and the "Connect & Get Health Records" button all compete for one row. Stepped rather than a single
  // value so a small laptop doesn't lose most of the page behind the drawer.
  // ⚠️ Deliberately kept under ~900px: ProviderOrgRow is CONTAINER-queried at MUI's `md`, so a drawer
  // that wide would flip every provider row from the stacked layout to the wide inline one.
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 560 }, bgcolor: BG } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #d7d3f0' }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#1c1c1e' }}>
          Health Wallet Settings
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close settings">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 5s long-press anywhere on the settings background reveals the Advanced dev section in prod
          (session-only). Mirrors the navbar's LongPressGuard → AuthProfileDialog reveal. */}
      <LongPressGuard onLongPress={() => setRevealAdvanced(true)}>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto' }}>

        {/* Health Records Sync Section */}
        {isAuthenticated && !isOnboardingLoading && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
              Health Records
            </Typography>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>

              {/* Last updated + refresh button */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isDataRequestComplete && lastCompletedAt
                  ? getLastUpdatedMessage(lastCompletedAt)
                  : 'Your health records have not been retrieved yet.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={isIasRequestPending ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <RefreshIcon />}
                disabled={isDataRequestPending}
                onClick={handleRestartWorkflow}
                sx={{
                  bgcolor: BRAND_DARKER,
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#5a3fc0' },
                  '&.Mui-disabled': { bgcolor: '#8a7ac0', color: 'white' },
                }}
              >
                {isIasRequestPending ? 'Retrieving...' : isDataRequestComplete ? 'Refresh health records' : 'Start retrieving records'}
              </Button>

              {/* Pending: progress bar + log toggle */}
              {isDataRequestPending && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Retrieving your health records…</Typography>
                    <Button size="small" onClick={() => setShowLog(v => !v)}
                      sx={{ color: BRAND_DARKER, textTransform: 'none', fontSize: '0.72rem', minWidth: 'auto', p: '2px 6px' }}>
                      {showLog ? 'Hide log' : 'Show log'}
                    </Button>
                  </Box>
                  <LinearProgress sx={{ borderRadius: 1, bgcolor: '#efeaff', '& .MuiLinearProgress-bar': { bgcolor: BRAND_DARKER } }} />
                </Box>
              )}

              {/* Failed state */}
              {isDataRequestFailed && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  We hit a problem retrieving your health records. Use the button above to retry, or{' '}
                  <Link component={RouterLink} to="/contact">contact support</Link>.
                  {onboardingStatus?.dataRequestError && (
                    <Typography variant="caption" component="div" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      Details: {onboardingStatus.dataRequestError}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* SSE log — keepMounted keeps this alive even when drawer is closed */}
              {sseEverShown && (
                <>
                  <Box sx={{ display: showLog ? 'block' : 'none', mt: 2 }}>
                    <Box sx={{ border: '1px solid #d7d3f0', borderRadius: 2, p: 1.5, bgcolor: '#f7f7fa', maxHeight: 220, overflowY: 'auto' }}>
                      <DataRequestSSEUpdates />
                    </Box>
                  </Box>
                  {!isDataRequestPending && (
                    <Box sx={{ mt: 1.5 }}>
                      <Button size="small" onClick={() => setShowLog(v => !v)}
                        sx={{ color: BRAND_DARKER, textTransform: 'none', fontSize: '0.75rem', p: '2px 6px' }}>
                        {showLog ? 'Hide retrieval log' : 'Show retrieval log'}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Paper>
          </Box>
        )}

        {/* Epic Connection Section */}
        {isAuthenticated && !isOnboardingLoading && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
              Provider Connections
            </Typography>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                {/* The "Login to MyChart" toggle MOVED to the bottom dev-toggle block (with Deduplicate
                    records — the pair gets removed together for prod). It stays GLOBAL for the per-org
                    Connect buttons: when on, force a fresh MyChart login instead of a stored Epic token. */}
                {/* COMMENTED OUT (not deleted) — the single "Refresh Epic Health Records" button.
                    Retrieval is now PER-ORG via the Connect list below (each org's FHIR endpoint from
                    relatedTransactions), so one blanket button no longer fits. Epic is building a
                    BLANKET auth option that authorizes ALL of a patient's orgs at once — expected ready
                    by year-end 2026. When that ships, restore this button to trigger the all-orgs auth.
                <Button
                  variant="contained"
                  startIcon={isEpicRequestPending ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <LinkIcon />}
                  disabled={isDataRequestPending}
                  onClick={() => handleEpicWorkflow()}
                  sx={{
                    bgcolor: BRAND_DARKER,
                    borderRadius: 50,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#5a3fc0' },
                    '&.Mui-disabled': { bgcolor: '#8a7ac0', color: 'white' },
                  }}
                >
                  {isEpicRequestPending ? 'Retrieving...' : 'Refresh Epic Health Records'}
                </Button>
                */}
              </Box>

              {/* Per-org connect: providers discovered to offer a direct FHIR (Epic) endpoint.
                  Each fires the same Epic OAuth flow, scoped to that org via { iasRequestId, orgIndex }. */}
              {/* OLD inline per-org markup — commented (not deleted) per Leo; superseded by the shared
                  ProviderOrgRow version below so the status labels match onboarding + records.
              {Array.isArray(onboardingStatus?.epicFhirOrgs) && onboardingStatus!.epicFhirOrgs!.length > 0 && (
                <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #ececf1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {onboardingStatus!.epicFhirOrgs!.map((org) => (
                    <Box key={`${org.requestId}:${org.index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1c1c1e' }} noWrap>
                          {org.displayName || 'Unknown provider'}
                        </Typography>
                        {org.address && (
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }} noWrap>
                            {org.address}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={(isEpicRequestPending && refreshingOrgKey === `${org.requestId}:${org.index}`) ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <LinkIcon />}
                        disabled={isDataRequestPending}
                        onClick={() => handleEpicWorkflow(org)}
                        sx={{
                          bgcolor: BRAND_DARKER,
                          borderRadius: 50,
                          textTransform: 'none',
                          fontWeight: 600,
                          flexShrink: 0,
                          '&:hover': { bgcolor: '#5a3fc0' },
                          '&.Mui-disabled': { bgcolor: '#8a7ac0', color: 'white' },
                        }}
                      >
                        Refresh
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
              */}

              {/* Shared ProviderOrgRow — SAME status chip (deriveOrgOutcome/OrgOutcomeChip) as onboarding +
                  records, next to the name; trailing = the per-org token-first Refresh (handleEpicWorkflow). */}
              {Array.isArray(onboardingStatus?.epicFhirOrgs) && onboardingStatus!.epicFhirOrgs!.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <ProviderOrgList
                    orgs={onboardingStatus!.epicFhirOrgs!}
                    selectedKey={highlightKey}
                    renderTrailing={(org) => (
                      <>
                      {/* Only renders for a provider the PATIENT added that has retrieved nothing. */}
                      <ProviderRemoveButton org={org} disabled={isDataRequestPending} />
                      <ProviderRefreshButton
                        busy={isEpicRequestPending && refreshingOrgKey === `${org.requestId}:${org.index}`}
                        disabled={isDataRequestPending}
                        // "Connect & …" for any org that isn't connected (incl. SKIPPED), plain
                        // "Get Health Records" once it has a token. See providerRetrieveLabel.
                        label={providerRetrieveLabel(org)}
                        onClick={() => handleEpicWorkflow(org)}
                      />
                      </>
                    )}
                  />
                </Box>
              )}

              {/* Same picker as onboarding, same behaviour. Arguably its more natural home: realising
                  "they never pulled my records from X" usually happens later, once the patient has
                  looked at what came back. Collapsed, so it stays out of the way until wanted.
                  ★ SETTINGS ONLY: onConnectNow offers the MyChart login straight from the success
                  notice — there is no batch here and no guided loop to re-enter, so the patient who
                  just named a provider is one click from the records they came for. Withheld while a
                  sync runs, because Settings forbids concurrent workflows. */}
              <MissingProviderPicker
                onAdded={handleProviderAdded}
                onAlreadyPresent={handleProviderAlreadyPresent}
                onConnectNow={(org) => handleEpicWorkflow(org)}
                connectBlockedReason={isDataRequestPending
                  ? 'You can connect it as soon as the sync that’s running finishes.'
                  : null}
              />
            </Paper>
          </Box>
        )}

        <Divider />

        {/* Browser Trust Section */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
            Browser Trust
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: '20px',
              bgcolor: trustEnabled ? '#f0fdf4' : 'white',
              border: '2px solid',
              borderColor: trustEnabled ? '#86efac' : '#d7d3f0',
              transition: 'all 0.3s ease',
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={trustEnabled}
                  onChange={(e) => handleTrustChange(e.target.checked)}
                  disabled={isTrustLoading}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#86efac' },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: trustEnabled ? '#166534' : '#1c1c1e' }}>
                      {trustEnabled ? 'This browser is trusted' : 'Do not trust this browser'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trustEnabled
                        ? 'You will be automatically authenticated on future visits.'
                        : 'You will need to verify your identity each time.'}
                    </Typography>
                  </Box>
                  {isTrustLoading && <CircularProgress size={16} sx={{ color: BRAND_DARKER, flexShrink: 0 }} />}
                </Box>
              }
              sx={{ m: 0, width: '100%', gap: 1.5, alignItems: 'flex-start' }}
            />
          </Paper>

          {isTrustError && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
              Failed to update browser trust. {(trustError as any)?.data?.message || 'Please try again.'}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <LaptopIcon sx={{ color: BRAND_DARKER, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>Only trust personal devices</Typography>
                <Typography variant="caption" color="text.secondary">Don't enable this on shared or public computers.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <TimerIcon sx={{ color: BRAND_DARKER, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>Trust expires after 90 days</Typography>
                <Typography variant="caption" color="text.secondary">You'll be prompted to re-verify periodically for security.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: '#efeaff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShieldIcon sx={{ color: BRAND_DARKER, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={600}>Revoke anytime</Typography>
                <Typography variant="caption" color="text.secondary">Toggle off at any time to require re-verification on your next visit.</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Consents — sits immediately above the delete section because withdrawing the REQUIRED consent
            is delete-HW-data (the delete nulls the consents column outright), so the two belong together.
            Only the optional research consent is revocable here; the required one renders checked+disabled
            and points down at the delete button. Revocation is ONE-WAY from Settings: re-granting needs a
            fresh signature + consent version, which is the onboarding consent flow, not a checkbox. */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: BRAND_DARKER, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
            Consents
          </Typography>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #ececf3' }}>
            {/* Required consent — visible so the user can see what they agreed to, disabled because
                the wallet cannot function without it. */}
            <FormControlLabel
              control={<Checkbox checked disabled sx={{ '&.Mui-disabled': { color: '#a3a3ad' } }} />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Health Wallet consent and record access</Typography>
                  {/* display:block so the caption uses its own line-height rather than the parent's
                      taller strut — keeps this row's spacing identical to the research consent below. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Required for the Health Wallet to work. To withdraw it, delete your Health Wallet data below.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 2, '& .MuiCheckbox-root': { pt: 0.25 } }}
            />

            <Divider sx={{ mb: 2 }} />

            {/* Optional research consent — unchecking withdraws it. */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={researchConsentGranted}
                  disabled={isRevoking || !researchConsentGranted}
                  onChange={handleRevokeResearchConsent}
                  sx={{ color: BRAND_DARK, '&.Mui-checked': { color: BRAND_DARKER } }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Research data sharing</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {researchConsent.label}.{' '}
                    <a href={`${process.env.PUBLIC_URL}/${researchConsent.doc}`} target="_blank" rel="noopener noreferrer" style={{ color: BRAND_DARKER, fontWeight: 600 }}>Read the consent</a>.
                  </Typography>
                  {!researchConsentGranted && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                      Not shared. Contact support if you would like to take part again.
                    </Typography>
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
            />

            {confirmRevoke && researchConsentGranted && (
              <Alert severity="warning" sx={{ borderRadius: 2, mt: 2 }}>
                Stop sharing your records for research? Click the checkbox again to confirm.
                Data already shared cannot be recalled.
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="text"
                    size="small"
                    disabled={isRevoking}
                    onClick={() => setConfirmRevoke(false)}
                    sx={{ color: 'text.secondary', borderRadius: 50, textTransform: 'none', fontWeight: 600 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Alert>
            )}
            {isRevoking && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={16} sx={{ color: BRAND_DARKER }} />
                <Typography variant="caption" color="text.secondary">Withdrawing…</Typography>
              </Box>
            )}
            {isRevokeError && (
              <Alert severity="error" sx={{ borderRadius: 2, mt: 2 }}>
                Couldn’t withdraw your consent. {(revokeError as any)?.data?.error || 'Please try again.'}
              </Alert>
            )}
          </Paper>
        </Box>

        <Divider />

        {/* Delete Health Wallet data. NO section heading, deliberately: the red border, tinted
            background and the copy already carry the warning, and "Danger Zone" reads as developer
            chrome on a patient-facing screen. */}
        <Box>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', border: '1px solid #fecaca', bgcolor: '#fff7f7' }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Delete Health Wallet Data</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Permanently remove all your health records, verification, and consent data. This cannot be undone.
            </Typography>
            {confirmDelete && (
              <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                Are you sure? Click again to permanently delete all data.
              </Alert>
            )}
            {isDeleteError && (
              <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
                {(deleteError as any)?.status === 409
                  ? 'A health data sync is in progress. Please try again later.'
                  : `Failed to delete data. ${(deleteError as any)?.data?.message || 'Please try again.'}`}
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={confirmDelete ? 'contained' : 'outlined'}
                startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <DeleteOutlineIcon />}
                disabled={isDeleting || profileId == null}
                onClick={handleDeleteClick}
                sx={{
                  color: confirmDelete ? 'white' : '#dc2626',
                  bgcolor: confirmDelete ? '#dc2626' : 'transparent',
                  borderColor: '#fca5a5',
                  borderRadius: 50,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: confirmDelete ? '#b91c1c' : 'rgba(220, 38, 38, 0.04)',
                    borderColor: '#dc2626',
                  },
                  '&.Mui-disabled': { color: '#fca5a5', borderColor: '#fecaca' },
                }}
              >
                {confirmDelete ? 'Confirm Delete' : 'Delete All Data'}
              </Button>
              {confirmDelete && (
                <Button
                  variant="text"
                  disabled={isDeleting}
                  onClick={() => setConfirmDelete(false)}
                  sx={{ color: 'text.secondary', borderRadius: 50, textTransform: 'none', fontWeight: 600 }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Box>

        {/* "Advanced" section — DEV toggles, HIDDEN IN PROD (Leo 2026-07-20; REACT_APP_ENV). read-time
            resource dedup — OFF shows the raw stored rows (cross-representation duplicates) on every records
            screen; instance-GLOBAL (flips HW's module var for all patients), SET-only backend, resting state
            TRUE both sides (project_hw_resource_dedup_feature). "Login to MyChart" (moved here from Additional
            Health Data — Leo) forces a fresh MyChart login instead of reusing a stored Epic token. */}
        {!hideAdvanced && (<>
        <Divider />
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.75rem' }}>
            Advanced
          </Typography>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: '20px', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dedupOn}
                onChange={(e) => handleDedupToggle(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_DARKER },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d7d3f0' },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: '#475569' }}>
                Deduplicate records
              </Typography>
            }
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={bypassEpicDataRequest}
                onChange={(e) => setBypassEpicDataRequest(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_DARKER },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d7d3f0' },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: '#475569' }}>
                Login to MyChart
              </Typography>
            }
            sx={{ m: 0 }}
          />
          {/* Force refetch + reprocess all CCDAs (Leo 2026-07-22): threads forceReprocessCcda into the
              data-request payload so the Epic workflow overwrites already-stored CCDAs (bypasses storeBinary
              write-once) — for converter iteration + manual currency. Kno2 already reprocesses every sync.
              Session-only, no storage. Does NOT touch on-demand/dedup logic. See project_hw_force_refetch_reprocess_ccda. */}
          <FormControlLabel
            control={
              <Switch
                checked={forceReprocessAll}
                onChange={(e) => setForceReprocessAll(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_DARKER },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d7d3f0' },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: '#475569' }}>
                Force refetch + reprocess all CCDAs
              </Typography>
            }
            sx={{ m: 0 }}
          />
          {/* AI summaries (Leo 2026-07-13): gates the chart-summary AI calls everywhere (legacy
              ValueChart + V1 trend details). MEMORY-ONLY flag — resets to ON on reload; backend
              persistence is the recorded follow-up (no client storage per standing rule). */}
          <FormControlLabel
            control={
              <Switch
                checked={aiSummaryOn}
                onChange={(e) => { setAiSummaryEnabled(e.target.checked); }}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_DARKER },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#d7d3f0' },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: '#475569' }}>
                AI summaries
              </Typography>
            }
            sx={{ m: 0 }}
          />
          </Box>
          </Paper>
        </Box>
        </>)}
      </Box>
      </LongPressGuard>
    </Drawer>
  );
};
